import { useState } from "react";
import { getSupabaseClient } from "../lib/supabase";

const brandPrimary = "#2C2731";
const brandSurface = "#FFFCEF";
const brandSurfaceAlt = "#F2EAC3";

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(44,39,49,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 50,
};

const modalStyle: React.CSSProperties = {
  width: "min(500px, 100%)",
  background: brandSurface,
  borderRadius: 24,
  border: `1px solid ${brandPrimary}`,
  padding: 24,
  boxShadow: "0 24px 48px rgba(44,39,49,0.35)",
  color: brandPrimary,
};

const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  color: brandPrimary,
  marginBottom: 16,
};

const textStyle: React.CSSProperties = {
  fontSize: 14,
  color: brandPrimary,
  lineHeight: 1.6,
  marginBottom: 24,
};

const warningBoxStyle: React.CSSProperties = {
  border: "1px solid #DC2626",
  borderRadius: 8,
  padding: 16,
  background: "#FEE2E2",
  marginBottom: 16,
};

const warningTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#991B1B",
  fontWeight: 500,
};

const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  justifyContent: "flex-end",
};

const buttonBaseStyle: React.CSSProperties = {
  padding: "10px 20px",
  borderRadius: 8,
  border: `1px solid ${brandPrimary}`,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const cancelButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: brandSurface,
  color: brandPrimary,
};

const confirmButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: "#DC2626",
  color: "#FFFFFF",
  borderColor: "#DC2626",
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: "#D1D5DB",
  color: "#6B7280",
  borderColor: "#D1D5DB",
  cursor: "not-allowed",
  opacity: 0.6,
};

const successBoxStyle: React.CSSProperties = {
  border: "1px solid #059669",
  borderRadius: 8,
  padding: 16,
  background: "#D1FAE5",
  marginBottom: 16,
};

const successTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#065F46",
  fontWeight: 500,
};

const errorBoxStyle: React.CSSProperties = {
  border: "1px solid #DC2626",
  borderRadius: 8,
  padding: 16,
  background: "#FEE2E2",
  marginBottom: 16,
};

const errorTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#991B1B",
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: 8,
  border: `1px solid ${brandPrimary}`,
  fontSize: 14,
  color: brandPrimary,
  background: brandSurface,
  outline: "none",
  marginBottom: 24,
};

interface DeleteUserModalProps {
  userId: string;
  seekerId?: string;
  userName: string;
  role: "guide" | "seeker";
  isActive: boolean;
  hasActiveMatch: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export default function DeleteUserModal({
  userId,
  seekerId,
  userName,
  role,
  isActive,
  hasActiveMatch,
  onClose,
  onDeleted,
}: DeleteUserModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canConfirm = confirmText.toLowerCase() === "delete" && !isLoading;

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      const body: Record<string, string> = { role };
      if (userId) body.user_id = userId;
      if (seekerId) body.seeker_id = seekerId;

      const { data, error } = await supabase.functions.invoke("delete-user-account", {
        method: "POST",
        body,
      });

      if (error) {
        throw new Error(error.message || "Failed to delete account");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setSuccess(true);
      setTimeout(() => {
        onDeleted();
      }, 1500);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err instanceof Error ? err.message : "Failed to delete account");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div style={modalStyle}>
        <h2 style={titleStyle}>Delete {roleLabel} Account</h2>

        {!success && (
          <>
            <div style={textStyle}>
              You are about to permanently delete <strong>{userName}</strong>'s {role} account. This will:
            </div>

            <div style={warningBoxStyle}>
              <div style={warningTextStyle}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Delete the user's authentication account</li>
                  <li>Remove their profile and all associated data</li>
                  <li>Delete all orders, offers, and payment records</li>
                  <li>Remove all matches, sessions, and notes</li>
                </ul>
              </div>
            </div>

            {(isActive || hasActiveMatch) && (
              <div style={{ ...warningBoxStyle, border: "2px solid #DC2626", background: "#FCA5A5" }}>
                <div style={{ ...warningTextStyle, fontSize: 14, color: "#7F1D1D" }}>
                  {role === "guide" && isActive && (
                    <div>This guide is currently <strong>ACTIVE</strong> with connected seekers.</div>
                  )}
                  {hasActiveMatch && (
                    <div>
                      This {role} has an <strong>active match</strong> with a {role === "guide" ? "seeker" : "guide"}.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ fontSize: 14, color: brandPrimary, marginBottom: 8 }}>
              Type <strong>delete</strong> to confirm:
            </div>
            <input
              style={inputStyle}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type delete to confirm"
              disabled={isLoading}
            />
          </>
        )}

        {error && (
          <div style={errorBoxStyle}>
            <div style={errorTextStyle}>{error}</div>
          </div>
        )}

        {success && (
          <div style={successBoxStyle}>
            <div style={successTextStyle}>
              {roleLabel} account deleted successfully.
            </div>
          </div>
        )}

        <div style={buttonRowStyle}>
          {!success && (
            <>
              <button
                style={cancelButtonStyle}
                onClick={onClose}
                disabled={isLoading}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = brandSurfaceAlt;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = brandSurface;
                }}
              >
                Cancel
              </button>
              <button
                style={!canConfirm ? disabledButtonStyle : confirmButtonStyle}
                onClick={handleDelete}
                disabled={!canConfirm}
                onMouseEnter={(e) => {
                  if (canConfirm) {
                    e.currentTarget.style.background = "#B91C1C";
                  }
                }}
                onMouseLeave={(e) => {
                  if (canConfirm) {
                    e.currentTarget.style.background = "#DC2626";
                  }
                }}
              >
                {isLoading ? "Deleting..." : "Delete Account"}
              </button>
            </>
          )}
          {success && (
            <button
              style={buttonBaseStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = brandSurfaceAlt;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = brandSurface;
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
